import * as React from 'react'

interface UseScrollToBottomVisibilityProps {
  messagesContainerRef: React.RefObject<HTMLDivElement>
  messagesEndRef?: React.RefObject<HTMLDivElement>
  threshold?: number
  isLoadingPrevious?: boolean
  contentLength?: number
}

export const useScrollToBottomVisibility = ({
  messagesContainerRef,
  messagesEndRef,
  threshold = 50,
  isLoadingPrevious = false,
  contentLength = 0,
}: UseScrollToBottomVisibilityProps) => {
  const [isScrolledUp, setIsScrolledUp] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const checkScrollPosition = React.useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    setIsScrolledUp(distanceFromBottom > threshold)
  }, [messagesContainerRef, threshold])

  React.useEffect(() => {
    const container = messagesContainerRef.current
    const endElement = messagesEndRef?.current
    if (!container) return

    if (endElement) {
      const intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          setIsScrolledUp(!entry.isIntersecting)
        },
        {
          root: container,
          threshold: 0,
          rootMargin: `0px 0px ${threshold}px 0px`,
        }
      )

      intersectionObserver.observe(endElement)
      return () => intersectionObserver.disconnect()
    }

    const handleScroll = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        checkScrollPosition()
      }, 10)
    }

    checkScrollPosition()
    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [messagesContainerRef, messagesEndRef, checkScrollPosition, threshold, contentLength])

  React.useEffect(() => {
    if (isLoadingPrevious) {
      setIsScrolledUp(true)
      return
    }

    const container = messagesContainerRef.current
    if (!container) return

    const timeoutId = setTimeout(checkScrollPosition, 200)
    return () => clearTimeout(timeoutId)
  }, [isLoadingPrevious, checkScrollPosition, messagesContainerRef])

  return { isScrolledUp }
}
